(ns ui.sticker-dialogs.emoji-picker
  "A Replicant emoji picker for the sticker dialogs, built on the data
   of the emoji-mart package (its `emojiIndex` search and category
   lists) instead of its React component. It renders native emoji
   characters, which is also what the dialogs insert into their text
   fields. Its state lives under `[:ui/modal :sticker :emoji-picker]`:

   - `:field` the dialog field the picked emoji goes to, `:dom-id` the
     id of its text field (to insert at the cursor), `:mode` `:insert`
     or `:replace` (the emoji of the emoji slider)
   - `:query` the search text, `:category` the shown category id and
     `:skin` the skin tone 1..6

   The `:sticker-dialog/emoji-*` action handlers at the end of the
   namespace keep that state."
  (:require ["emoji-mart" :as emojimart]
            ["emoji-mart/data/google.json" :as emoji-data]
            [clojure.string :as str]
            [ui.sticker-dialogs.actions :as actions]
            [ui.sticker-dialogs.model :as model]))

(def frequent
  "The emojis shown first."
  ["heart" "heart_eyes" "kissing_heart" "joy" "grinning" "blush" "sunglasses"
   "two_hearts" "wink" "kiss" "100" "grin" "stuck_out_tongue_winking_eye" "v"
   "+1" "ok_hand" "fire" "angry" "scream" "disappointed_relieved" "raised_hands"
   "tada" "hankey" "pray" "open_mouth" "sleeping" "sob" "unamused" "flushed"
   "weary" "neutral_face" "innocent" "mask" "clap" "eyes" "dog" "cat" "pig"
   "see_no_evil" "hear_no_evil" "speak_no_evil" "broken_heart"
   "full_moon_with_face" "new_moon_with_face" "sun_with_face" "crown" "rainbow"
   "moneybag"])

(def category-labels
  {"people" "Smileys & People"
   "nature" "Animals & Nature"
   "foods" "Food & Drink"
   "activity" "Activity"
   "places" "Travel & Places"
   "objects" "Objects"
   "symbols" "Symbols"
   "flags" "Flags"})

(def categories
  "`[id label emoji-ids]` of the emoji-mart categories, frequent first."
  (into [["frequent" "Frequent" frequent]]
        (map (fn [category]
               [(.-id category)
                (get category-labels (.-id category) (.-name category))
                (vec (.-emojis category))])
             (.-categories emoji-data))))

(def skins
  "The skin tones of emoji-mart: 1 is the neutral yellow."
  [["1" "#ffc93a"]
   ["2" "#fadcbc"]
   ["3" "#e0bb95"]
   ["4" "#bf8f68"]
   ["5" "#9b643d"]
   ["6" "#594539"]])

(defn native
  "The native character of the emoji `id` in the `skin` tone (emoji
   without skin variants ignore it)."
  [id skin]
  (when-let [^js emoji (aget (.-emojis emojimart/emojiIndex) id)]
    (if (.-native emoji)
      (.-native emoji)
      (when-let [^js variant (or (aget emoji (str skin))
                                 (aget emoji "1"))]
        (.-native variant)))))

(defn search
  "The native characters matching `query`, via the emoji-mart index."
  [query skin]
  (->> (.search emojimart/emojiIndex query)
       (map (fn [emoji]
              (native (.-id emoji) skin)))
       (remove nil?)
       (distinct)
       (vec)))

(defn emojis-to-show
  [{:keys [query category skin]}]
  (if (seq (str/trim (str query)))
    (search query (or skin 1))
    (let [[_ _ ids] (or (some (fn [[id :as entry]]
                                (when (= id (or category "frequent"))
                                  entry))
                              categories)
                        (first categories))]
      (->> ids
           (map #(native % (or skin 1)))
           (remove nil?)
           (distinct)
           (vec)))))

(defn emoji-cell
  [emoji]
  [:button.text-2xl.leading-none.p-1.rounded.hover:bg-gray-200
   {:replicant/key emoji
    :type "button"
    :on {:click [[:js/prevent-default]
                 [:sticker-dialog/emoji-select emoji]]}}
   emoji])

(defn panel
  "The picker popover, rendered by a dialog next to its text field
   `field` while the picker is open for that field. Sits below the field
   (the host element needs `relative` positioning)."
  [{:keys [emoji-picker]} field]
  (when (and emoji-picker
             (= field (:field emoji-picker)))
    (let [{:keys [query category skin]} emoji-picker
          emojis (emojis-to-show emoji-picker)]
      [:div
       [:div.fixed.inset-0.z-10
        {:on {:click [[:sticker-dialog/emoji-close]]}}]
       [:div.absolute.right-0.z-20.w-80.bg-white.rounded-lg.shadow-xl.border.border-gray-200.p-2.text-left
        {:style {:top "100%"}}
        [:input.w-full.border.border-gray-300.rounded.px-2.py-1.text-sm.mb-2
         {:type "text"
          :placeholder "Search emojis"
          :value (or query "")
          :replicant/on-mount (fn [{:keys [replicant/node]}]
                                (.focus node))
          :on {:input [[:sticker-dialog/emoji-query :event/target.value]]}}]
        [:div.flex.flex-wrap.gap-1.mb-2
         (map (fn [[id label]]
                [:button.text-xs.px-2.py-1.rounded
                 {:replicant/key id
                  :type "button"
                  :class (if (= id (or category "frequent"))
                           ["bg-storrito-500" "text-white"]
                           ["bg-gray-100" "text-gray-700" "hover:bg-gray-200"])
                  :on {:click [[:js/prevent-default]
                               [:sticker-dialog/emoji-category id]]}}
                 label])
              categories)]
        [:div.flex.flex-wrap.content-start.overflow-y-auto
         {:style {:height "220px"}}
         (if (seq emojis)
           (map emoji-cell emojis)
           [:span.text-sm.text-gray-500.p-2 "No emojis found."])]
        [:div.flex.items-center.gap-1.mt-2
         [:span.text-xs.text-gray-500.mr-1 "Skin tone"]
         (map (fn [[id color]]
                [:button.w-5.h-5.rounded-full.border-2
                 {:replicant/key id
                  :type "button"
                  :title (str "Skin tone " id)
                  :class (if (= id (str (or skin 1)))
                           ["border-gray-700"]
                           ["border-transparent"])
                  :style {:background-color color}
                  :on {:click [[:js/prevent-default]
                               [:sticker-dialog/emoji-skin (js/parseInt id)]]}}])
              skins)]]])))

(defn select!
  "Inserts the picked emoji at the cursor of the field the picker was
   opened for (or replaces the field for the emoji of the slider)."
  [{:keys [store action]}]
  (let [[_ emoji] action
        dialog (actions/dialog @store)
        {:keys [field dom-id mode]} (:emoji-picker dialog)
        node (when dom-id
               (js/document.getElementById dom-id))
        text (or (model/field-value dialog field) "")
        value (if (= :replace mode)
                emoji
                (model/insert-at text
                                 (some-> node .-selectionStart)
                                 (some-> node .-selectionEnd)
                                 emoji))]
    (actions/swap-dialog! store (fn [dialog]
                                  (-> dialog
                                      (model/set-field field value)
                                      (dissoc :emoji-picker))))))

(def handlers
  [(actions/handler :sticker-dialog/emoji-open
                    (fn [{:keys [store action]}]
                      (let [[_ field dom-id mode] action]
                        (actions/swap-dialog! store assoc :emoji-picker {:field field
                                                                         :dom-id dom-id
                                                                         :mode (or mode :insert)
                                                                         :query ""
                                                                         :category "frequent"
                                                                         :skin 1}))))
   (actions/handler :sticker-dialog/emoji-close
                    (fn [{:keys [store]}]
                      (actions/swap-dialog! store dissoc :emoji-picker)))
   (actions/handler :sticker-dialog/emoji-query
                    (fn [{:keys [store action]}]
                      (actions/swap-dialog! store assoc-in [:emoji-picker :query] (second action))))
   (actions/handler :sticker-dialog/emoji-category
                    (fn [{:keys [store action]}]
                      (actions/swap-dialog! store update :emoji-picker assoc
                                            :category (second action)
                                            :query "")))
   (actions/handler :sticker-dialog/emoji-skin
                    (fn [{:keys [store action]}]
                      (actions/swap-dialog! store assoc-in [:emoji-picker :skin] (second action))))
   (actions/handler :sticker-dialog/emoji-select select!)])
